
import React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "@/lib/context/auth-context";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { addComment } from "@/lib/services/support-ticket-service";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

const formSchema = z.object({
  comment: z.string().min(1, { message: "Comment cannot be empty" }),
  is_internal: z.boolean().default(false)
});

type FormValues = z.infer<typeof formSchema>;

interface TicketCommentFormProps {
  ticketId: string;
  isStaff?: boolean;
}

const TicketCommentForm: React.FC<TicketCommentFormProps> = ({ 
  ticketId,
  isStaff = false
}) => {
  const { profile } = useAuth();
  
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      comment: "",
      is_internal: false
    }
  });
  
  const onSubmit = async (values: FormValues) => {
    if (!profile) return;
    
    const commentData = {
      ticket_id: ticketId,
      comment: values.comment,
      user_id: profile.id,
      is_internal: isStaff ? values.is_internal : false
    };
    
    const result = await addComment(commentData);
    
    if (result) {
      form.reset();
    }
  };
  
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="comment"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Textarea 
                  placeholder="Add a comment..." 
                  className="min-h-[100px]"
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        {isStaff && (
          <div className="flex items-center space-x-2">
            <FormField
              control={form.control}
              name="is_internal"
              render={({ field }) => (
                <FormItem className="flex items-center space-x-2">
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <Label htmlFor="is-internal" className="text-sm">Internal note (only visible to staff)</Label>
                </FormItem>
              )}
            />
          </div>
        )}
        
        <div className="flex justify-end">
          <Button type="submit">Add Comment</Button>
        </div>
      </form>
    </Form>
  );
};

export default TicketCommentForm;
